import React from 'react';
import moment from 'moment';
import {
  oneDecimalPercentFormatValidateFloatTrue,
  twoDecimalPercentFormat,
  yearsDecimalFormat,
} from 'common/formats/formats';
import { SelectValueViewer } from 'components';
import { GridDate, GridDateEditor, GridDateValueViewer, GridSelect } from 'components/FeaturedSpreadsheet/components';
import {
  MATURITY_ALIAS,
  MATURITY_CUSTOM_KEY,
  MATURITY_DATE_ALIAS,
  MATURITY_DATE_TITLE,
  MATURITY_TITLE,
  OPM_BACKSOLVE_DATE_ALIAS,
  OPM_BACKSOLVE_DATE_CUSTOM_KEY,
  OPM_BACKSOLVE_DATE_TITLE,
  RISK_ALIAS,
  RISK_FREE_RATE_CUSTOM_KEY,
  RISK_TITLE,
  VOLATILITY_ALIAS,
  VOLATILITY_CUSTOM_KEY,
  VOLATILITY_SOURCE_ALIAS,
  VOLATILITY_SOURCE_TITLE,
  VOLATILITY_TITLE,
} from 'pages/Valuations/approaches/backsolveApproach/OPMInputTable/constants';
import { getObjectValue, gridShortDate } from 'utilities';
import { ParserProps } from './types';

export default ({ tableData }: ParserProps) => {
  const data = getObjectValue(tableData.data);
  const backsolveDate = gridShortDate(data.opm_backsolve_date ?? tableData.measurementDate.date);
  const maturity = data[MATURITY_ALIAS as keyof typeof data];
  const { isDisabled, volatilitySourceOptions } = tableData;
  const cells = {
    A1: {
      key: 'A1',
      value: OPM_BACKSOLVE_DATE_TITLE,
    },
    A2: {
      key: 'A2',
      value: MATURITY_TITLE,
    },
    A3: {
      key: 'A3',
      value: MATURITY_DATE_TITLE,
    },
    A4: {
      key: 'A4',
      value: VOLATILITY_SOURCE_TITLE,
    },
    A5: {
      key: 'A5',
      value: VOLATILITY_TITLE,
    },
    A6: {
      key: 'A6',
      value: RISK_TITLE,
    },
    B1: {
      key: 'B1',
      value: backsolveDate,
      alias: OPM_BACKSOLVE_DATE_ALIAS,
      readOnly: isDisabled,
      gridType: 'gridDate',
      dataEditor: (props: any) => <GridDateEditor {...props} />,
      valueViewer: (props: any) => <GridDateValueViewer {...props} />,
      gridDateComponent: GridDate,
      format: null,
      isRequired: true,
      dbType: 'dbDate',
      customKey: OPM_BACKSOLVE_DATE_CUSTOM_KEY,
    },
    B2: {
      alias: MATURITY_ALIAS,
      customKey: MATURITY_CUSTOM_KEY,
      dbType: 'string',
      format: {
        ...yearsDecimalFormat,
        maximumFractionDigits: 2,
        maximumFractionDigitsWhenEditing: 4,
      },
      greaterThanIfOPM: 0,
      gridType: 'number',
      isRequiredIfOPM: true,
      key: 'B2',
      readOnly: isDisabled,
      value: maturity,
    },
    B3: {
      alias: MATURITY_DATE_ALIAS,
      gridType: 'string',
      key: 'B3',
      readOnly: true,
      value:
        data.maturity_date
        ?? moment(backsolveDate, 'MM/DD/YYYY').clone().add(Number(maturity), 'years').format('MM/DD/YYYY'),
    },
    B4: {
      key: 'B4',
      value: data.volatility_source ?? data.reference_for_volatility ?? null,
      alias: VOLATILITY_SOURCE_ALIAS,
      readOnly: isDisabled,
      dbType: 'number',
      dropdown: true,
      dataEditor: (props: any) => <GridSelect menuPosition="absolute" options={volatilitySourceOptions} {...props} />,
      valueViewer: (props: any) => <SelectValueViewer options={volatilitySourceOptions} {...props} />,
    },
    B5: {
      alias: VOLATILITY_ALIAS,
      customKey: VOLATILITY_CUSTOM_KEY,
      dbDecimalPlaces: 4,
      dbType: 'string',
      format: oneDecimalPercentFormatValidateFloatTrue,
      greaterThanIfOPM: 0,
      gridType: 'percentage',
      isRequiredIfOPM: true,
      key: 'B5',
      readOnly: isDisabled,
      value: data[VOLATILITY_ALIAS],
    },
    B6: {
      alias: RISK_ALIAS,
      customKey: RISK_FREE_RATE_CUSTOM_KEY,
      dbDecimalPlaces: 6,
      dbType: 'string',
      format: twoDecimalPercentFormat({
        validateFloat: true,
      }),
      gridType: 'percentage',
      key: 'B6',
      value: data[RISK_ALIAS as keyof typeof data],
    },
  };
  return cells;
};
